// components/DoctorScheduleCalendar.jsx
import React, { useEffect, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import axiosInstance from "../../utils/axiosInstance";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./calendar-mobile.css";
import { useNavigate } from "react-router-dom";

const localizer = momentLocalizer(moment);

function DoctorScheduleCalendar({ doctorId, refreshTrigger }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [currentView, setCurrentView] = useState("month");
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchExpandedSchedules = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(
          `/doctor/${doctorId}/expanded-schedules`
        );

        const formattedEvents = res.data
          .filter((dateInfo) => dateInfo && dateInfo.date)
          .map((dateInfo) => {
            // Use the actual start and end times from the schedule
            const start = moment(
              `${dateInfo.date} ${dateInfo.startTime}`,
              "YYYY-MM-DD HH:mm"
            );
            const end = moment(
              `${dateInfo.date} ${dateInfo.endTime}`,
              "YYYY-MM-DD HH:mm"
            );

            // Create title with available slots count
            const title = `${dateInfo.availableSlots} / ${dateInfo.totalSlots} Available`;

            return {
              id: `event-${dateInfo.date}`,
              title: String(title),
              start: start.toDate(),
              end: end.toDate(),
              allDay: false, // Set to false to show time
              resource: dateInfo,
            };
          })
          .filter((event) => {
            const isValid =
              event &&
              event.title &&
              typeof event.title === "string" &&
              event.start instanceof Date &&
              event.end instanceof Date &&
              !isNaN(event.start.getTime()) &&
              !isNaN(event.end.getTime());

            if (!isValid) {
              console.warn("Filtered out invalid event:", event);
            }
            return isValid;
          });

        console.log("Formatted events:", formattedEvents);
        console.log("Total valid events:", formattedEvents.length);
        setEvents(formattedEvents);
      } catch (error) {
        console.error("Failed to fetch expanded schedules:", error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    if (doctorId) {
      fetchExpandedSchedules();
    }
  }, [doctorId, refreshTrigger]);

  const CustomEvent = ({ event }) => {
    if (!event || !event.title) return null;

    return (
      <div className="flex flex-col leading-tight overflow-hidden">
        <span
          className={`font-semibold text-yellow-300 truncate ${
            isMobile ? "text-xs" : "text-sm"
          }`}
        >
          {event.title}
        </span>
        {!isMobile && event.start && event.end && (
          <span className="text-xs text-gray-200 mt-1 truncate">
            {moment(event.start).format("hh.mm A")} -{" "}
            {moment(event.end).format("hh.mm A")}
          </span>
        )}
      </div>
    );
  };

  const CustomAgendaEvent = ({ event }) => {
    if (!event || !event.title) return <span>No title</span>;
    return <span>{event.title}</span>;
  };

  const eventPropGetter = (event) => {
    return {
      style: {
        backgroundColor: "#3b82f6",
        borderRadius: "4px",
        opacity: 0.8,
        color: "white",
        border: "0px",
        display: "block",
      },
    };
  };

  const calendarStyle = {
    height: isMobile ? 350 : 600,
    minHeight: isMobile ? 250 : 300,
    minWidth: isMobile ? "100%" : "auto",
    width: "100%",
  };

  return (
    <div className="bg-white p-2 sm:p-4 md:p-6 rounded-lg shadow border w-full">
      <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 px-1 sm:px-0">
        Doctor's Schedule Calendar
      </h2>

      {/* Mobile View Tip */}
      {isMobile && (
        <div className="mb-3 px-2 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-700">
            💡 <strong>Tip:</strong> Scroll to view all available dates. Tap on
            any date to view slots.
          </p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-3"></div>
            <p className="text-gray-600">Loading schedules...</p>
          </div>
        </div>
      ) : events.length === 0 ? (
        <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No schedules available</p>
        </div>
      ) : (
        <div className="calendar-container w-full overflow-hidden">
          {isMobile ? (
            // Mobile: Show simplified date cards with slot counts
            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-blue-500 scrollbar-track-gray-200 pt-1">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-3 text-white shadow-sm sticky top-0 z-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="font-semibold text-sm">
                      Available Dates
                    </span>
                  </div>
                  <span className="bg-white/20 px-2 py-1 rounded-full text-xs font-medium">
                    {
                      Object.keys(
                        events
                          .filter((event) =>
                            moment(event.start).isSameOrAfter(moment(), "day")
                          )
                          .reduce((groups, event) => {
                            const date = moment(event.start).format(
                              "YYYY-MM-DD"
                            );
                            groups[date] = true;
                            return groups;
                          }, {})
                      ).length
                    }{" "}
                    {Object.keys(
                      events
                        .filter((event) =>
                          moment(event.start).isSameOrAfter(moment(), "day")
                        )
                        .reduce((groups, event) => {
                          const date = moment(event.start).format("YYYY-MM-DD");
                          groups[date] = true;
                          return groups;
                        }, {})
                    ).length === 1
                      ? "day"
                      : "days"}
                  </span>
                </div>
              </div>

              {Object.entries(
                events
                  .sort((a, b) => a.start - b.start)
                  .filter((event) =>
                    moment(event.start).isSameOrAfter(moment(), "day")
                  ) // Filter to show only today onwards
                  .reduce((groups, event) => {
                    const date = moment(event.start).format("YYYY-MM-DD");
                    if (!groups[date]) groups[date] = [];
                    groups[date].push(event);
                    return groups;
                  }, {})
              ).map(([date, dayEvents], index) => {
                const isToday = moment(date).isSame(moment(), "day");
                return (
                  <div
                    key={date}
                    onClick={() => {
                      navigate(
                        `/dashboard/doctor/${doctorId}/schedules/${date}/slots`
                      );
                    }}
                    className={`bg-white border border-gray-200 rounded-lg shadow-sm cursor-pointer hover:shadow-md transition active:scale-98 relative ${
                      isToday ? "today-date-card" : "overflow-hidden"
                    }`}
                  >
                    <div className="p-4">
                      <div className="flex items-center gap-4">
                        {/* Calendar Icon with Date */}
                        <div className="flex-shrink-0">
                          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex flex-col items-center justify-center text-white shadow-md">
                            <span className="text-xs font-medium uppercase">
                              {moment(date).format("MMM")}
                            </span>
                            <span className="text-2xl font-bold">
                              {moment(date).format("DD")}
                            </span>
                          </div>
                        </div>

                        {/* Date Info */}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-900 text-base">
                            {moment(date).format("dddd")}
                          </div>
                          <div className="text-sm text-gray-600 mt-0.5">
                            {moment(date).format("MMMM DD, YYYY")}
                          </div>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                              <svg
                                className="w-3.5 h-3.5 mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                              {dayEvents[0]?.resource?.availableSlots || 0}{" "}
                              Available
                            </div>
                            {dayEvents[0]?.resource?.bookedSlots > 0 && (
                              <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                                {dayEvents[0].resource.bookedSlots} Booked
                              </div>
                            )}
                            {dayEvents[0]?.resource?.blockedSlots > 0 && (
                              <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                                {dayEvents[0].resource.blockedSlots} Blocked
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Arrow Icon */}
                        <svg
                          className="flex-shrink-0 w-6 h-6 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Desktop: Show calendar or custom list view
            <>
              {currentView === "agenda" ? (
                // Custom List View
                <div className="space-y-4 max-h-[700px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-blue-500 scrollbar-track-gray-200 pt-1">
                  <div className="flex items-center justify-between mb-4 sticky top-0 bg-white z-10 py-2">
                    <h3 className="text-lg font-semibold text-gray-800">
                      Available Dates
                    </h3>
                    <button
                      onClick={() => setCurrentView("month")}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
                    >
                      ← Back to Calendar
                    </button>
                  </div>
                  {Object.entries(
                    events
                      .sort((a, b) => a.start - b.start)
                      .filter((event) =>
                        moment(event.start).isSameOrAfter(moment(), "day")
                      ) // Filter to show only today onwards
                      .reduce((groups, event) => {
                        const date = moment(event.start).format("YYYY-MM-DD");
                        if (!groups[date]) groups[date] = [];
                        groups[date].push(event);
                        return groups;
                      }, {})
                  ).map(([date, dayEvents], index) => {
                    const isToday = moment(date).isSame(moment(), "day");
                    return (
                      <div
                        key={date}
                        onClick={() => {
                          navigate(
                            `/dashboard/doctor/${doctorId}/schedules/${date}/slots`
                          );
                        }}
                        className={`bg-white border border-gray-200 rounded-lg shadow-sm cursor-pointer hover:shadow-md transition relative ${
                          isToday ? "today-date-card" : "overflow-hidden"
                        }`}
                      >
                        <div className="p-4">
                          <div className="flex items-center gap-4">
                            {/* Calendar Icon with Date */}
                            <div className="flex-shrink-0">
                              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex flex-col items-center justify-center text-white shadow-md">
                                <span className="text-xs font-medium uppercase">
                                  {moment(date).format("MMM")}
                                </span>
                                <span className="text-3xl font-bold">
                                  {moment(date).format("DD")}
                                </span>
                              </div>
                            </div>

                            {/* Date Info */}
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-gray-900 text-lg">
                                {moment(date).format("dddd")}
                              </div>
                              <div className="text-sm text-gray-600 mt-0.5">
                                {moment(date).format("MMMM DD, YYYY")}
                              </div>
                              <div className="flex items-center gap-2 mt-2 flex-wrap">
                                <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold bg-green-100 text-green-700">
                                  <svg
                                    className="w-4 h-4 mr-1.5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                  {dayEvents[0]?.resource?.availableSlots || 0}{" "}
                                  Available
                                </div>
                                {dayEvents[0]?.resource?.bookedSlots > 0 && (
                                  <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold bg-blue-100 text-blue-700">
                                    {dayEvents[0].resource.bookedSlots} Booked
                                  </div>
                                )}
                                {dayEvents[0]?.resource?.blockedSlots > 0 && (
                                  <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold bg-red-100 text-red-700">
                                    {dayEvents[0].resource.blockedSlots} Blocked
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Arrow Icon */}
                            <svg
                              className="flex-shrink-0 w-6 h-6 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                // Calendar View
                <Calendar
                  localizer={localizer}
                  events={events}
                  startAccessor="start"
                  endAccessor="end"
                  titleAccessor="title"
                  views={["month", "agenda"]}
                  view={currentView}
                  onView={(view) => setCurrentView(view)}
                  defaultView="month"
                  selectable
                  popup
                  onSelectSlot={(slotInfo) => {
                    const dateStr = moment(slotInfo.start).format("YYYY-MM-DD");
                    navigate(
                      `/dashboard/doctor/${doctorId}/schedules/${dateStr}/slots`
                    );
                  }}
                  onSelectEvent={(event) => {
                    const dateStr = moment(event.start).format("YYYY-MM-DD");
                    navigate(
                      `/dashboard/doctor/${doctorId}/schedules/${dateStr}/slots`
                    );
                  }}
                  components={{
                    event: CustomEvent,
                  }}
                  eventPropGetter={eventPropGetter}
                  style={calendarStyle}
                  messages={{
                    agenda: "List View",
                    month: "Month",
                    today: "Today",
                    previous: "Back",
                    next: "Next",
                    noEventsInRange: "No schedules in this range",
                  }}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default DoctorScheduleCalendar;
